import React, {
  ReactNode, useContext, useEffect, useState,
} from 'react'
import { VoucherProps } from '../PaidMediaLandingPage/MediaPageHero/MediaPageVoucher'
import { TimeslotData } from '../../api/BookingCalendar'
import { newOrderFlowStore } from '../../sessionStorage'
import { PersonalDetailsFormData } from './steps/CheckoutStep/PersonalDetailsForm/PersonalDetailsForm'
import { PaymentData } from './steps/CheckoutStep/PaymentDetails/PaymentDetails'
import {
  ChannelType, PlatformType, SegmentType,
} from '../../utils/commonEnums'
import { useRouter } from 'next/router'
import { clearCart } from '../../pages/friends'
import { OTSFormProps } from '../../api/OTSForm'

export const THREE_GIGA_IDS = [
  '5f919991ec3439005733a8e2',
  '6051e3cd00d048002a168014',
]

export interface OrderOptions {
  broadband?: any;
  voice?: any | null;
  tv?: any | null;
  wier?: any | null;
  vas?: any | null;
  bundle?: any | null;
  callPackage?: number;
  router: boolean;
  connector: boolean;
  data: PersonalDetailsFormData;
  voucher_flags?: VoucherProps[];
  discount?: any | null;
  promo?: any | null;
  timeslot: TimeslotData | null;
  timeslotError: string;
  countDownTimerStart: number | null;
  basketExpiryTimer?: boolean;
  isDetailsStep: boolean;
  reachedInstallationStep: boolean;
  earliestTimeslot: TimeslotData | null;
  paymentDetails?: PaymentData;
  paymentError: string;
  placeOrderError: string;
  referralSource?: string;
  activeContractLength?: number;
  activeContractLengthTab: number;
  accountId?: string;
  hasSelectedNoVoice?: boolean;
  hasSelectedNoTV?: boolean;
  hasSelectedNoWIER?: boolean;
  hasSelectedNoVAS?: boolean;
  appliedReferralCode?: string;
  appliedProductId?: string;
  hasClosedAbandonedCartModal: boolean;
  showDifferentContractError?: boolean;
  hasPortedNumber?: boolean;
  portedNumber: string;
  previousProvider: string;
  error_5000_url: string;
  error_5001_url: string;
  error_5003_url: string;
  default_error_url: string;
  reservationId: string;
  alternativePhone?: boolean;
  cartInstanceIdentifier: string;
  defaultRafId?: string;
  channel: string;
  utmSource?: string;
  cks?: string;
  hide_strikethrough?: boolean;
  packagesTerm: {
    [SegmentType.RESIDENTIAL]: number | null;
    [SegmentType.BUSINESS]: number | null;
    [SegmentType.STUDENT]: number | null;
  };
  voucherProduct: any | null;
  offer: string | null;
  dcmsEligibility: boolean;
  platform: PlatformType;
  promoToApply?: string | null;
  oneTouchSwitching: boolean;
  switchingConfirmation: boolean;
  switchingConfirmationError: boolean;
  OTSPhone: boolean;
  OTSMatchRequestError: boolean;
  OTSFormProps?: OTSFormProps;
  OTSOptOut: boolean;
  OTSResubmitTriggered: boolean;
  butterCheckoutContent?: any;
  filterLabel: string;
}

export const INITIAL_ORDER_OPTIONS: OrderOptions = {
  router: false,
  connector: false,
  data: {
    sprn: '',
    address: '',
    firstName: '',
    lastName: '',
    phone: '',
    contactMarketing: false,
    email: '',
    contactEmail: false,
    contactPhone: false,
    contactSMS: false,
    contactPost: false,
    readTerms: false,
    promo: '',
  },
  voucher_flags: [],
  discount: null,
  promo: null,
  countDownTimerStart: null,
  basketExpiryTimer: false,
  timeslot: null,
  timeslotError: '',
  isDetailsStep: true,
  reachedInstallationStep: false,
  earliestTimeslot: null,
  paymentError: '',
  placeOrderError: '',
  hasSelectedNoVoice: false,
  hasSelectedNoTV: false,
  hasSelectedNoWIER: false,
  hasSelectedNoVAS: false,
  hasClosedAbandonedCartModal: false,
  hasPortedNumber: false,
  portedNumber: '',
  previousProvider: '',
  activeContractLength: 24,
  activeContractLengthTab: 0,
  error_5000_url: '/order-successful-act-account',
  error_5001_url: '/order-unsuccessful-salesstatus',
  error_5003_url: '/order-successful-installer',
  default_error_url: '/order-unsuccessful',
  reservationId: '',
  alternativePhone: true,
  cartInstanceIdentifier: '',
  defaultRafId: 'Refer_friend',
  channel: ChannelType.DIGITAL,
  utmSource: undefined,
  cks: undefined,
  packagesTerm: {
    [SegmentType.RESIDENTIAL]: null,
    [SegmentType.BUSINESS]: null,
    [SegmentType.STUDENT]: null,
  },
  voucherProduct: null,
  offer: null,
  dcmsEligibility: false,
  platform: PlatformType.CF,
  promoToApply: null,
  oneTouchSwitching: true,
  switchingConfirmation: false,
  switchingConfirmationError: false,
  OTSPhone: false,
  OTSMatchRequestError: false,
  OTSOptOut: false,
  OTSResubmitTriggered: false,
  filterLabel: '',
}

interface OrderContextType {
  options: OrderOptions;
  setOptions: React.Dispatch<React.SetStateAction<OrderOptions>>;
  isLoaded: boolean;
}

const OrderContext = React.createContext<OrderContextType | null>(null)

export function useOrderContext() {
  const context = useContext(OrderContext)

  if (!context) {
    throw new Error('Ensure you use inside OrderContext Provider')
  }

  return context
}

export function OrderContextProvider({ children }: { children: ReactNode }) {
  const { query } = useRouter()
  const router = useRouter()
  const [options, setOptions] = useState<OrderOptions>(INITIAL_ORDER_OPTIONS)
  const [isLoaded, setIsLoaded] = useState(false)
  const [cartTimer, setCartTimer] = useState<NodeJS.Timeout | null>(null)

  // Load saved order from storage on mount
  useEffect(() => {
    async function fetchSavedOrder() {
      try {
        const savedOrder = await newOrderFlowStore.get()        
        if (savedOrder && Object.keys(savedOrder).length > 0) {
          setOptions(savedOrder)
        }
      } catch (error) {
        console.error('🚨 [INIT] Failed to fetch saved order:', error)
      } finally {
        setIsLoaded(true)
      }
    }

    fetchSavedOrder()
  }, []) // Only run once on mount

  // Cart inactivity timer with proper cleanup
  useEffect(() => {
    if (cartTimer) {
      clearTimeout(cartTimer)
      setCartTimer(null)
    }

    if (isLoaded && options.cartInstanceIdentifier && options.cartInstanceIdentifier !== '') {
      
      const timeoutId = setTimeout(() => {
        setOptions((state) => ({
          ...state,
          ...clearCart,
        }))
        router.push('/session-expired')
        newOrderFlowStore.delete()
      }, 30 * 60 * 1000) // 30 minutes

      setCartTimer(timeoutId)
    }

    return () => {
      if (cartTimer) {
        clearTimeout(cartTimer)
      }
    }
  }, [options.cartInstanceIdentifier, isLoaded])

  // Persist order state to storage
  useEffect(() => {
    async function persistOrderState() {
      // Don't persist if we haven't loaded from storage yet
      if (!isLoaded) {
        return
      }
      
      // Check if options have actually changed from initial
      const hasChanges = Object.keys(options).some(key => {
        const current = options[key as keyof OrderOptions]
        const initial = INITIAL_ORDER_OPTIONS[key as keyof OrderOptions]
        return JSON.stringify(current) !== JSON.stringify(initial)
      })

      if (hasChanges) {
        try {
          // Handle localStorage basketExpiryTime
          const now = new Date().getTime()
          const newOrderFlow = localStorage.getItem('new-order-flow')
          const basketExpiryTime = localStorage.getItem('basketExpiryTime')
          
          if (newOrderFlow) {
            if (!basketExpiryTime || JSON.stringify(options) !== newOrderFlow) {
              localStorage.setItem('basketExpiryTime', JSON.stringify(now))
            }
          }

          await newOrderFlowStore.set(options)
        } catch (error) {
          console.error('🚨 [PERSIST] Failed to persist options:', error)
        }
      }
    }

    persistOrderState()
  }, [options, isLoaded])

  // Handle affiliate/UTM parameters
  useEffect(() => {
    async function fetchAffiliateGeneralContext() {
      const utmSource = query?.utm_source as string
      const cks = query?.awc as string
      
      if (utmSource && cks) {
        setOptions(state => ({
          ...state,
          utmSource,
          cks,
        }))
      }
    }

    if (isLoaded) {
      fetchAffiliateGeneralContext()
    }
  }, [query, isLoaded])

  // Don't render children until storage is loaded
  if (!isLoaded) {
    return null
  }

  return (
    <OrderContext.Provider value={{
      options,
      setOptions,
      isLoaded,
    }}>
      {children}
    </OrderContext.Provider>
  )
}
